/**
 * The control that lets a visitor overrule the system's answer about motion.
 *
 * It only appears when it can change something a person would notice — which, in
 * practice, means it appears for everyone whose machine asks for stillness and would
 * otherwise get a site of still photographs with no sign that any of it moves.
 *
 * Changing it re-renders the index rather than trying to retrofit the running page:
 * scenes decide at construction whether to build a video element at all, and half of
 * the difference lives in the stylesheet.
 */

import type { UiCopy } from "./i18n";
import { motionAllowed, setMotion } from "./motion";

export type MotionToggle = { element: HTMLElement };

export function createMotionToggle(copy: UiCopy, onChange: () => void): MotionToggle {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "ambience motion-toggle";
  button.title = copy.motionHint;

  const icon = document.createElement("span");
  icon.className = "motion-toggle__icon";
  icon.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className = "ambience__label";

  const paint = (): void => {
    const on = motionAllowed();
    button.setAttribute("aria-pressed", on ? "true" : "false");
    button.dataset.on = on ? "true" : "false";
    label.textContent = on ? copy.motionOn : copy.motionOff;
    // The state is carried by aria-pressed, so the accessible name stays put; renaming
    // it would announce a different control every time it is used.
    button.setAttribute("aria-label", copy.motionLabel);
  };

  button.addEventListener("click", () => {
    setMotion(!motionAllowed());
    paint();
    onChange();
  });

  button.append(icon, label);
  paint();
  return { element: button };
}
